import {PeachPayOrder, setOrderError} from './../../reducers/peachPayOrderReducer';
import {startModalProcessing, stopModalLoading} from './../../reducers/environmentReducer';
import {type ITransactionService, type OrderService} from './../order';
import {type PayPalNamespace, type PayPalButtonsComponent, type CreateOrderData, type CreateOrderActions, type OnApproveData, type OnApproveActions, type FUNDING_SOURCE, type PayPalButtonsComponentOptions, type OnCancelledActions} from '@paypal/paypal-js';
import {$qs} from '../../../../@shared/ts/dom';
import {getErrorString} from '../../../../@shared/ts/error';
import {FeatureFlag} from '../../../../@type/features';
import {Feature} from '../../reducers/environmentReducer';
import {type PayPalPaymentData} from './paypal';
import {store} from '../../store';
import {updateCustomerDataInBrowser} from '../../features/customer';
import {clearLocalSession} from '../session';
import {PaymentConfiguration} from '../../reducers/paymentConfigurationReducer';
import {updateCustomerPreferredGateway} from '../../reducers/peachPayCustomerReducer';
import {getLocaleText} from '../../util/translation';

class PayPalPaymentButton {
	private readonly paypal: PayPalNamespace;
	private readonly orderService: OrderService;
	private readonly gatewayId: string;
	private readonly buttonStyle: PayPalButtonsComponentOptions['style'];
	private readonly fundingType: FUNDING_SOURCE;

	private button: PayPalButtonsComponent | null;
	private mountContainer: HTMLDivElement | null;
	private isMounted: boolean;

	private currentPaymentDetails: PayPalPaymentData | null;
	private currentTransaction: ITransactionService | null;

	constructor(paypal: PayPalNamespace, orderService: OrderService, options: {
		gatewayId: string;
		fundingType: FUNDING_SOURCE;
		buttonStyle: PayPalButtonsComponentOptions['style'];
	}) {
		this.paypal = paypal;
		this.orderService = orderService;
		this.gatewayId = options.gatewayId;
		this.fundingType = options.fundingType;
		this.buttonStyle = options.buttonStyle;

		this.isMounted = false;
		this.mountContainer = null;

		this.currentPaymentDetails = null;
		this.currentTransaction = null;

		this.button = this.createButtonComponent();
	}

	public isEligible(): boolean {
		return this.button?.isEligible() ?? false;
	}

	public mount(selector: string) {
		if (this.isMounted) {
			return;
		}

		const $container = $qs(selector);
		if (!$container) {
			return;
		}

		if (!this.button) {
			this.button = this.createButtonComponent();
		}

		if (this.mountContainer) {
			this.mountContainer?.classList.remove('hide');
		} else {
			this.mountContainer = document.createElement('div');
			$container.insertAdjacentElement('beforeend', this.mountContainer);
			this.button?.render(this.mountContainer).catch(() => { /**/});
		}

		this.isMounted = true;
	}

	public unmount() {
		if (!this.isMounted) {
			return;
		}

		this.mountContainer?.remove();
		this.mountContainer = null;
		this.button = null;
		this.isMounted = false;
	}

	private createButtonComponent(): PayPalButtonsComponent | null {
		const {orderService} = this;
		const gateway = this;
		// eslint-disable-next-line new-cap
		return this.paypal?.Buttons?.({
			fundingSource: this.fundingType,
			style: this.buttonStyle,
			onClick() {
				store.dispatch(startModalProcessing());
			},
			async createOrder(_data: CreateOrderData, _actions: CreateOrderActions) {
				gateway.currentTransaction = await orderService.startTransaction(gateway.gatewayId);
				if (!gateway.currentTransaction) {
					store.dispatch(stopModalLoading());
					return '';
				}

				try {
					const order = await orderService.placeOrder(gateway.currentTransaction, {
						peachpay_session_id: PeachPayOrder.sessionId(),
						peachpay_transaction_id: gateway.currentTransaction.getId(),
					});

					if (order.result === 'failure') {
						await gateway.currentTransaction.complete();
						store.dispatch(stopModalLoading());
						return '';
					}

					const dataURL = new URL(order.redirect);
					const [name, data] = dataURL.hash.split('=');
					if (name !== '#payment_data' || !data) {
						await gateway.currentTransaction.complete({
							note: 'Failed to retrieve paypal payment details from url: ' + order.redirect,
						});
						store.dispatch(stopModalLoading());
						return '';
					}

					const paymentData = JSON.parse(atob(decodeURIComponent(data))) as PayPalPaymentData;

					gateway.currentPaymentDetails = paymentData;
					return paymentData.data.id;
				} catch (error) {
					await gateway.currentTransaction.complete({
						note: 'Failed creating paypal order: ' + getErrorString(error),
					});
					return '';
				}
			},
			async onApprove(_data: OnApproveData, actions: OnApproveActions) {
				if (!gateway.currentPaymentDetails?.order_id) {
					await gateway.currentTransaction?.complete({
						note: 'Failed approving paypal order: "Missing payment details"',
					});
					actions.restart();
					store.dispatch(stopModalLoading());
					return;
				}

				const approveURL = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'approve_order_url');

				if (!approveURL) {
					await gateway.currentTransaction?.complete({
						note: 'Failed approving paypal order: "Missing required approve URL or approve URL security nonce"',
					});
					actions.restart();
					store.dispatch(stopModalLoading());
					return;
				}

				const formData = new FormData();
				formData.append('order_id', gateway.currentPaymentDetails.order_id);

				const response = await fetch(approveURL, {
					method: 'POST',
					body: formData,
				});

				try {
					const json = await response.json() as IResponse<void>;
					if (!json.success) {
						await gateway.currentTransaction?.complete({
							note: json.message,
						});
						actions.restart();
						store.dispatch(stopModalLoading());
						return;
					}
				} catch (error) {
					const paypalOrder = await actions.order?.get();
					if (paypalOrder?.status !== 'APPROVED' && paypalOrder?.status !== 'COMPLETED') {
						store.dispatch(setOrderError(getLocaleText('PayPal order failed to finish.')));
						await gateway.currentTransaction?.complete({
							note: `PayPal order failed to finish: ${getErrorString(error)}`,
						});
						actions.restart();
						store.dispatch(stopModalLoading());
						return;
					}
				}

				store.dispatch(updateCustomerPreferredGateway(PaymentConfiguration.selectedGateway()));

				updateCustomerDataInBrowser();
				clearLocalSession();

				if (window.top) {
					window.top.location.href = gateway.currentPaymentDetails.success_url;
				} else {
					actions.redirect(gateway.currentPaymentDetails.success_url);
				}
			},
			async onCancel(_data: Record<string, unknown>, _actions: OnCancelledActions) {
				if (!gateway.currentPaymentDetails) {
					await gateway.currentTransaction?.complete({
						paymentStatus: 'CANCELED',
						orderStatus: 'unknown',
						note: 'Payment canceled by customer. Order status "unknown" because of missing payment details.',
					});

					store.dispatch(stopModalLoading());
					return;
				}

				await gateway?.currentTransaction?.complete({
					paymentStatus: 'CANCELED',
					orderStatus: 'canceled',
				});

				cancelOrder(gateway.currentPaymentDetails.cancel_url);

				store.dispatch(stopModalLoading());
			},
			async onError(error: Record<string, unknown>) {
				await gateway.currentTransaction?.complete({
					note: getErrorString(error),
				});

				gateway.currentTransaction = null;
				gateway.currentPaymentDetails = null;
				store.dispatch(stopModalLoading());
			},
		}) ?? null;
	}
}

function cancelOrder(url: string) {
	try {
		const xhr = new XMLHttpRequest();
		xhr.open('GET', url);
		xhr.send();
	} catch {
		// Do no harm
	}
}

export {
	PayPalPaymentButton,
};
